import { useState, useEffect, useCallback } from 'react';
import {
  MdClose,
  MdUpload,
  MdAdd,
  MdMenuBook,
  MdScience,
  MdCalculate,
  MdPublic,
  MdMusicNote,
  MdBrush,
  MdPsychology,
  MdCode
} from 'react-icons/md';
import { parentAPI } from '../../utils/api';
import InfoIcon from '../common/InfoIcon';
import WalkthroughTour from '../walkthrough/WalkthroughTour';
import { addSubjectSteps } from '../walkthrough/tourSteps';
import { shouldShowTour } from '../../config/tourConfig';
import { useAuth } from '../../contexts/AuthContext';

const X = MdClose as any;
const Upload = MdUpload as any;
const Plus = MdAdd as any;
const BookOpen = MdMenuBook as any;
const Flask = MdScience as any;
const Calculator = MdCalculate as any;
const Globe = MdPublic as any;
const MusicNote = MdMusicNote as any;
const PaintBrush = MdBrush as any;
const Brain = MdPsychology as any;
const Atom = MdScience as any;
const Code = MdCode as any;

const iconMap = {
  BookOpen,
  Flask,
  Calculator,
  Globe,
  MusicNote,
  PaintBrush,
  Brain,
  Atom,
  Code,
};

// Map subject names to icons
const getIconForSubject = (subjectName: string): string => {
  const normalizedName = subjectName.toLowerCase();

  if (normalizedName.includes('literature') || normalizedName.includes('english') || normalizedName.includes('reading')) return 'BookOpen';
  if (normalizedName.includes('chemistry')) return 'Flask';
  if (normalizedName.includes('math') || normalizedName.includes('algebra') || normalizedName.includes('calculus')) return 'Calculator';
  if (normalizedName.includes('geography') || normalizedName.includes('history')) return 'Globe';
  if (normalizedName.includes('music')) return 'MusicNote';
  if (normalizedName.includes('art') || normalizedName.includes('design')) return 'PaintBrush';
  if (normalizedName.includes('psychology') || normalizedName.includes('psych')) return 'Brain';
  if (normalizedName.includes('physics') || normalizedName.includes('science')) return 'Atom';
  if (normalizedName.includes('programming') || normalizedName.includes('computer') || normalizedName.includes('coding')) return 'Code';

  return 'BookOpen';
};

interface Subject {
  id: string;
  name: string;
  courses: Array<{
    id: string;
    name: string;
    school_name: string | null;
  }>;
}

interface ManageSubjectsModalProps {
  child: {
    id: string;
    name: string;
    email: string;
  };
  onClose: () => void;
  onSubjectAdded: () => void;
}

export default function ManageSubjectsModal({ child, onClose, onSubjectAdded }: ManageSubjectsModalProps) {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Tour integration
  const { userProfile, refreshProfile } = useAuth();
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    fetchChildSubjects();
  }, [child.id]);

  // Check if tour should show when add form is opened
  useEffect(() => {
    if (showAddForm && userProfile && shouldShowTour(userProfile.tour_add_subject, userProfile.tour_enabled, 'add-subject')) {
      setShowTour(true);
    } else {
      setShowTour(false);
    }
  }, [showAddForm, userProfile]);

  const fetchChildSubjects = async () => {
    try {
      setIsLoading(true);
      const response = await parentAPI.getChildProfile(child.id);
      setSubjects(response.subjects || []);
    } catch (error) {
      console.error('Failed to fetch child subjects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubjectAdded = async () => {
    setShowAddForm(false);
    await fetchChildSubjects();
    onSubjectAdded();
  };

  // Stable callback references for tour handlers
  const handleTourComplete = useCallback(() => {
    setShowTour(false);
    refreshProfile();
  }, [refreshProfile]);

  const handleTourSkip = useCallback(() => {
    setShowTour(false);
    refreshProfile();
  }, [refreshProfile]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#111827] rounded-lg p-6 max-w-4xl w-full mx-4 border border-gray-600 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-white text-xl font-bold">Manage Subjects - {child.name}</h2>
            <p className="text-gray-400 text-sm">{child.email}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-white text-lg">Loading subjects...</div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Existing Subjects */}
            {subjects.length > 0 && (
              <div>
                <h3 className="text-white text-lg font-bold mb-4">Enrolled Subjects</h3>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(158px,1fr))] gap-3">
                  {subjects.map((subject) => {
                    const Icon = iconMap[getIconForSubject(subject.name) as keyof typeof iconMap];
                    return (
                      <div
                        key={subject.id}
                        className="flex flex-col gap-3 rounded-lg p-4 bg-[#1a1f2e] border border-gray-700"
                      >
                        <div className="flex items-center gap-3">
                          <div className="text-white flex items-center justify-center rounded-lg bg-primary/20 h-12 w-12">
                            <Icon className="w-6 h-6" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white text-base font-bold truncate">{subject.name}</h3>
                            <p className="text-gray-400 text-sm truncate">
                              {subject.courses.length} {subject.courses.length === 1 ? 'course' : 'courses'}
                            </p>
                          </div>
                        </div>
                        {subject.courses.map((course) => (
                          <div key={course.id} className="pl-2 border-l-2 border-gray-600">
                            <p className="text-white text-sm">{course.name}</p>
                            {course.school_name && (
                              <p className="text-gray-500 text-xs">{course.school_name}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add Subject Section */}
            {!showAddForm ? (
              <div className="flex justify-center">
                <button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-secondary text-white rounded hover:bg-secondary/90 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Add New Subject
                </button>
              </div>
            ) : (
              <AddSubjectForm
                childId={child.id}
                onCancel={() => setShowAddForm(false)}
                onSuccess={handleSubjectAdded}
              />
            )}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-transparent border border-gray-600 text-white rounded hover:bg-gray-800 transition-colors"
          >
            Close
          </button>
        </div>

        {/* Add Subject Tour */}
        {showTour && showAddForm && (
          <WalkthroughTour
            section="add-subject"
            steps={addSubjectSteps}
            isActive={true}
            onComplete={handleTourComplete}
            onSkip={handleTourSkip}
          />
        )}
      </div>
    </div>
  );
}

interface AddSubjectFormProps {
  childId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

function AddSubjectForm({ childId, onCancel, onSuccess }: AddSubjectFormProps) {
  const [formData, setFormData] = useState({
    subjectName: '',
    courseName: '',
    schoolName: '',
    syllabusUrl: '',
  });
  const [syllabusFile, setSyllabusFile] = useState<File | null>(null);
  const [uploadMethod, setUploadMethod] = useState<'url' | 'file'>('url');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (file: File) => {
    if (file.type === 'application/pdf') {
      setSyllabusFile(file);
      setError(null);
    } else {
      setError('Please upload a PDF file');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await parentAPI.addSubjectToChild(
        childId,
        formData.subjectName,
        formData.courseName,
        formData.schoolName || undefined,
        uploadMethod === 'url' ? formData.syllabusUrl || undefined : undefined,
        uploadMethod === 'file' ? syllabusFile || undefined : undefined
      );
      onSuccess();
    } catch (err: any) {
      console.error('Failed to add subject:', err);
      setError(err.message || 'Failed to add subject. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#1a1f2e] rounded-lg p-6 border border-gray-700">
      <h3 className="text-white text-lg font-bold mb-4">Add New Subject</h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Subject Name */}
        <div>
          <label className="block text-gray-300 text-sm mb-2 font-semibold">
            Subject Name *
            <InfoIcon 
              title="Subject Name"
              content='Just for your reference (e.g., "Math", "Science"). This helps you identify the subject on your dashboard.'
            />
          </label>
          <input
            type="text"
            value={formData.subjectName}
            onChange={(e) => setFormData({ ...formData, subjectName: e.target.value })}
            placeholder="e.g., Math, Science, English"
            className="w-full px-3 py-2 bg-[#0f1419] border border-gray-600 rounded text-white focus:outline-none focus:border-primary"
            required
          />
        </div>

        {/* Course Name */}
        <div>
          <label className="block text-gray-300 text-sm mb-2 font-semibold">
            Course Name *
            <InfoIcon 
              title="Course Name"
              content='Official course name (e.g., "AP Calculus", "IB AA Mathematics HL", "Math 100"). This helps us find the right syllabus and course content.'
            />
          </label>
          <input
            type="text"
            value={formData.courseName}
            onChange={(e) => setFormData({ ...formData, courseName: e.target.value })}
            placeholder="e.g., AP Calculus BC, IB Math HL, CHEM 101"
            className="w-full px-3 py-2 bg-[#0f1419] border border-gray-600 rounded text-white focus:outline-none focus:border-primary"
            required
          />
        </div>

        {/* School Name */}
        <div>
          <label className="block text-gray-300 text-sm mb-2 font-semibold">
            School Name (Optional)
            <InfoIcon 
              title="School Name"
              content='Helps us find the syllabus for generic course names like "Math 100". Include your school or university name for more accurate results.'
            />
          </label>
          <input
            type="text"
            value={formData.schoolName}
            onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })}
            placeholder="e.g., Harvard University, MIT, UCLA"
            className="w-full px-3 py-2 bg-[#0f1419] border border-gray-600 rounded text-white focus:outline-none focus:border-primary"
          />
        </div>

        {/* Syllabus Upload Method Toggle */}
        <div>
          <label className="block text-gray-300 text-sm mb-2 font-semibold">
            Syllabus (Optional)
            <InfoIcon 
              title="Syllabus"
              content="Provide a syllabus URL or upload a PDF. We'll automatically extract course topics and objectives to make sure we teach you everything you need to learn for your course."
            />
          </label>
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => setUploadMethod('url')}
              className={`flex-1 px-4 py-2 rounded transition-colors ${
                uploadMethod === 'url'
                  ? 'bg-primary text-white'
                  : 'bg-[#0f1419] text-gray-400 border border-gray-600'
              }`}
            >
              URL
            </button>
            <button
              type="button"
              onClick={() => setUploadMethod('file')}
              className={`flex-1 px-4 py-2 rounded transition-colors ${
                uploadMethod === 'file'
                  ? 'bg-primary text-white'
                  : 'bg-[#0f1419] text-gray-400 border border-gray-600'
              }`}
            >
              Upload PDF
            </button>
          </div>

          {uploadMethod === 'url' ? (
            <input
              type="url"
              value={formData.syllabusUrl}
              onChange={(e) => setFormData({ ...formData, syllabusUrl: e.target.value })}
              placeholder="https://example.com/syllabus.pdf"
              className="w-full px-3 py-2 bg-[#0f1419] border border-gray-600 rounded text-white focus:outline-none focus:border-primary"
            />
          ) : (
            <div>
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  isDragging
                    ? 'border-primary bg-primary/10'
                    : 'border-gray-600 bg-[#0f1419]'
                }`}
              >
                {syllabusFile ? (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 mx-auto text-green-500" />
                    <p className="text-white font-semibold">{syllabusFile.name}</p>
                    <p className="text-gray-400 text-sm">
                      {(syllabusFile.size / 1024).toFixed(2)} KB
                    </p>
                    <button
                      type="button"
                      onClick={() => setSyllabusFile(null)}
                      className="text-red-500 hover:text-red-400 text-sm"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 mx-auto text-gray-400" />
                    <p className="text-gray-300">
                      Drag and drop your PDF here, or{' '}
                      <label className="text-primary hover:text-primary/80 cursor-pointer">
                        browse
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileSelect(file);
                          }}
                          className="hidden"
                        />
                      </label>
                    </p>
                    <p className="text-gray-500 text-sm">PDF files only</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500 rounded p-3 text-red-500 text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 bg-transparent border border-gray-600 text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Adding...
              </>
            ) : (
              'Add Subject'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
